<script lang="ts">
  /**
   * App - Root component with screen routing
   */

  import { fade, scale } from 'svelte/transition';
  import { cubicOut } from 'svelte/easing';
  import { gameState } from '$lib/stores/gameState';
  import { getRandomWord } from '$lib/data/wordBank';
  import StartScreen from '$lib/components/screens/StartScreen.svelte';
  import DifficultySelectScreen from '$lib/components/screens/DifficultySelectScreen.svelte';
  import LetterSelectScreen from '$lib/components/screens/LetterSelectScreen.svelte';
  import LetterModeScreen from '$lib/components/screens/LetterModeScreen.svelte';
  import MonsterModeScreen from '$lib/components/screens/MonsterModeScreen.svelte';
  import CelebrationScreen from '$lib/components/screens/CelebrationScreen.svelte';
  import SettingsOverlay from '$lib/components/ui/SettingsOverlay.svelte';
  import AboutOverlay from '$lib/components/ui/AboutOverlay.svelte';

  // Local state for UI
  let state = $state({
    showSettings: false,
    showAbout: false,
    showDifficultySelect: false,
    showLetterSelect: false,
    showCelebration: false
  });

  // Navigation handlers
  function handleSelectLetterMode() {
    // Show letter selection screen
    state.showLetterSelect = true;
  }

  function handleLetterSelected(letter: string) {
    // Start Letter Mode with selected letter or combination
    state.showLetterSelect = false;
    gameState.startLetterMode(letter);
  }

  function handleBackFromLetterSelect() {
    state.showLetterSelect = false;
  }

  function handleSelectMonsterMode() {
    // Show difficulty selection screen
    state.showDifficultySelect = true;
  }

  function handleSelectDifficulty(difficulty: 'easy' | 'medium' | 'advanced') {
    // Get a random word for the selected difficulty
    const word = getRandomWord(difficulty);

    if (word) {
      gameState.startMonsterMode(difficulty, word);
      state.showDifficultySelect = false;
    } else {
      console.error(`No words available for difficulty: ${difficulty}`);
    }
  }

  function handleBackFromDifficulty() {
    state.showDifficultySelect = false;
  }

  function handleOpenSettings() {
    state.showSettings = true;
  }

  function handleCloseSettings() {
    state.showSettings = false;
  }

  function handleOpenAbout() {
    state.showAbout = true;
  }

  function handleCloseAbout() {
    state.showAbout = false;
  }

  function handleBackToMenu() {
    state.showCelebration = false;
    gameState.returnToMenu();
  }

  function handleMonsterModeComplete() {
    state.showCelebration = true;
  }

  function handlePlayAgain() {
    const difficulty = $gameState.mode.difficulty || 'easy';
    const word = getRandomWord(difficulty);
    if (word) {
      state.showCelebration = false;
      gameState.startMonsterMode(difficulty, word);
    }
  }

  function handleChangeMode() {
    state.showCelebration = false;
    gameState.returnToMenu();
  }

  // Transition configuration
  const transitionConfig = {
    duration: 400,
    easing: cubicOut
  };
</script>

<main class="app">
  <!-- Start Screen (Menu) -->
  {#if $gameState.mode.mode === 'menu'}
    {#if state.showDifficultySelect}
      <div transition:fade={transitionConfig}>
        <DifficultySelectScreen
          onSelectDifficulty={handleSelectDifficulty}
          onBack={handleBackFromDifficulty}
        />
      </div>
    {:else if state.showLetterSelect}
      <div transition:fade={transitionConfig}>
        <LetterSelectScreen
          onSelectLetter={handleLetterSelected}
          onBack={handleBackFromLetterSelect}
        />
      </div>
    {:else}
      <div transition:fade={transitionConfig}>
        <StartScreen
          onSelectLetterMode={handleSelectLetterMode}
          onSelectMonsterMode={handleSelectMonsterMode}
          onOpenSettings={handleOpenSettings}
          onOpenAbout={handleOpenAbout}
        />
      </div>
    {/if}
  {/if}

  <!-- Letter Mode (Epic 3) -->
  {#if $gameState.mode.mode === 'letter'}
    <div transition:fade={transitionConfig}>
      <LetterModeScreen onBack={handleBackToMenu} />
    </div>
  {/if}

  <!-- Monster Mode (Epic 4) -->
  {#if $gameState.mode.mode === 'monster'}
    {#if state.showCelebration}
      <div transition:fade={transitionConfig}>
        <CelebrationScreen
          word={$gameState.targetWord?.word || ''}
          emoji={$gameState.targetWord?.emoji || ''}
          score={$gameState.score}
          onPlayAgain={handlePlayAgain}
          onChangeMode={handleChangeMode}
          onHome={handleBackToMenu}
        />
      </div>
    {:else}
      <div transition:fade={transitionConfig}>
        <MonsterModeScreen
          onBack={handleBackToMenu}
          onComplete={handleMonsterModeComplete}
        />
      </div>
    {/if}
  {/if}

  <!-- Settings Overlay -->
  <SettingsOverlay
    visible={state.showSettings}
    onClose={handleCloseSettings}
  />

  <!-- About Overlay -->
  <AboutOverlay
    visible={state.showAbout}
    onClose={handleCloseAbout}
  />
</main>

<style>
  .app {
    width: 100vw;
    height: 100vh;
    margin: 0;
    padding: 0;
    overflow: hidden;
  }
</style>
